import { EnvironmentId } from "@cmmd-forge/contracts";
import { classifyEnvironmentId } from "../environmentIdClassification";

import {
  getSavedEnvironmentRecord,
  listSavedEnvironmentRecords,
  persistSavedEnvironmentRecord,
  readSavedEnvironmentBearerToken,
  removeSavedEnvironmentBearerToken,
  replaceSavedEnvironmentRecord,
  type SavedEnvironmentRecord,
  writeSavedEnvironmentBearerToken,
} from "./catalog";
import { setSavedEnvironmentRegistry } from "~/clientPersistenceApi";
import { throwIfAborted } from "./abortableWait";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";

/**
 * Carry a record's known pull-request capability forward across a rewrite.
 *
 * A record rebuilt from a fresh runtime descriptor does not carry the
 * capability flag, so upserting it raw would blank out a capability the user
 * already authenticated for. Prefer what is persisted; fall back to what the
 * live runtime most recently reported.
 */
export function mergeSavedEnvironmentCapability(
  record: SavedEnvironmentRecord,
): SavedEnvironmentRecord {
  const persisted = getSavedEnvironmentRecord(record.environmentId);
  if (persisted?.lastAuthenticatedPullRequestCapability !== undefined) {
    return {
      ...record,
      lastAuthenticatedPullRequestCapability: persisted.lastAuthenticatedPullRequestCapability,
    };
  }

  const runtime = useSavedEnvironmentRuntimeStore.getState().byId?.[record.environmentId];
  return runtime?.authState === "authenticated" && runtime.descriptor
    ? {
        ...record,
        lastAuthenticatedPullRequestCapability:
          runtime.descriptor.capabilities.pullRequests === true,
      }
    : record;
}

export function isCmmdCloudEnvironment(record: SavedEnvironmentRecord): boolean {
  return Boolean(record.runtimeId?.trim()) || classifyEnvironmentId(record.environmentId).isCloud;
}

export function hasSameSavedEnvironmentConnectionTarget(
  left: SavedEnvironmentRecord,
  right: SavedEnvironmentRecord,
): boolean {
  return (
    left.environmentId === right.environmentId &&
    left.httpBaseUrl === right.httpBaseUrl &&
    left.wsBaseUrl === right.wsBaseUrl &&
    left.runtimeId === right.runtimeId &&
    left.cmmdOrganizationId === right.cmmdOrganizationId
  );
}

/**
 * Every cloud send registers the connection its execution intent returned. When that is the
 * target already on record, the caller keeps its live socket: removing it drops the retained
 * thread detail subscriptions and aborts the send that asked for it.
 */
export function registersConnectedTarget(input: {
  readonly existing: SavedEnvironmentRecord | null;
  readonly replacedRecord: SavedEnvironmentRecord | null;
  readonly record: SavedEnvironmentRecord;
}): boolean {
  return (
    input.replacedRecord === null &&
    input.existing !== null &&
    hasSameSavedEnvironmentConnectionTarget(input.existing, input.record)
  );
}

export function resolveSavedEnvironmentReplacement(input: {
  readonly environmentId: EnvironmentId;
  readonly replacesEnvironmentId: string | undefined;
}): {
  readonly existing: SavedEnvironmentRecord | null;
  readonly replacedRecord: SavedEnvironmentRecord | null;
} {
  const replacesEnvironmentId = input.replacesEnvironmentId
    ? EnvironmentId.make(input.replacesEnvironmentId)
    : null;
  const replacedRecord =
    replacesEnvironmentId && replacesEnvironmentId !== input.environmentId
      ? getSavedEnvironmentRecord(replacesEnvironmentId)
      : null;
  return {
    existing: getSavedEnvironmentRecord(input.environmentId) ?? replacedRecord,
    replacedRecord,
  };
}

export interface CloudPersistenceRollback {
  readonly records: readonly SavedEnvironmentRecord[];
  readonly bearerToken: string | null;
}

export async function captureCloudPersistenceRollback(
  existing: SavedEnvironmentRecord | null,
): Promise<CloudPersistenceRollback> {
  return {
    records: listSavedEnvironmentRecords(),
    bearerToken: existing ? await readSavedEnvironmentBearerToken(existing.environmentId) : null,
  };
}

interface CloudPersistenceTransaction {
  readonly record: SavedEnvironmentRecord;
  readonly replacedRecord: SavedEnvironmentRecord | null;
  readonly rollback: CloudPersistenceRollback;
  readonly signal?: AbortSignal;
}

export async function restoreCloudEnvironmentPersistence(
  input: Omit<CloudPersistenceTransaction, "signal">,
): Promise<void> {
  const previousEnvironmentId = input.replacedRecord?.environmentId ?? input.record.environmentId;
  await setSavedEnvironmentRegistry(input.rollback.records);
  await removeSavedEnvironmentBearerToken(input.record.environmentId).catch(() => undefined);
  if (input.rollback.bearerToken) {
    await writeSavedEnvironmentBearerToken(previousEnvironmentId, input.rollback.bearerToken);
  }
}

export async function persistConnectedCloudEnvironment(
  input: CloudPersistenceTransaction,
): Promise<void> {
  try {
    if (input.replacedRecord) {
      await replaceSavedEnvironmentRecord(input.replacedRecord.environmentId, input.record);
    } else {
      await persistSavedEnvironmentRecord(input.record);
    }
    throwIfAborted(input.signal);
    await removeSavedEnvironmentBearerToken(input.record.environmentId);
    if (input.replacedRecord) {
      await removeSavedEnvironmentBearerToken(input.replacedRecord.environmentId);
    }
    throwIfAborted(input.signal);
  } catch (error) {
    try {
      await restoreCloudEnvironmentPersistence(input);
    } catch (rollbackError) {
      const failure = new AggregateError(
        [error, rollbackError],
        "Cloud Environment rollback failed.",
      );
      failure.cause = error;
      throw failure;
    }
    throw error;
  }
}

/** Progress of one retry while a connected cloud Environment is still bootstrapping. */
export interface ConnectedSavedEnvironmentBootstrapRetry {
  readonly attempt: number;
  readonly maxAttempts: number;
  readonly retryDelayMs: number;
  readonly error: unknown;
}

/** The runtime store fields that record why an Environment's connection failed, and when. */
export function getRuntimeErrorFields(error: unknown) {
  return {
    lastError: error instanceof Error ? error.message : String(error),
    lastErrorAt: new Date().toISOString(),
  } as const;
}
